// src/index.ts
import { defineApp } from "@ex0/kernel";
var V86_WIDTH = 720;
var V86_HEIGHT = 480;
var BOOT_TIMEOUT_MS = 9e4;
var CODE_TO_SCANCODE = {
  Escape: 1,
  Digit1: 2,
  Digit2: 3,
  Digit3: 4,
  Digit4: 5,
  Digit5: 6,
  Digit6: 7,
  Digit7: 8,
  Digit8: 9,
  Digit9: 10,
  Digit0: 11,
  Minus: 12,
  Equal: 13,
  Backspace: 14,
  Tab: 15,
  KeyQ: 16,
  KeyW: 17,
  KeyE: 18,
  KeyR: 19,
  KeyT: 20,
  KeyY: 21,
  KeyU: 22,
  KeyI: 23,
  KeyO: 24,
  KeyP: 25,
  BracketLeft: 26,
  BracketRight: 27,
  Enter: 28,
  ControlLeft: 29,
  KeyA: 30,
  KeyS: 31,
  KeyD: 32,
  KeyF: 33,
  KeyG: 34,
  KeyH: 35,
  KeyJ: 36,
  KeyK: 37,
  KeyL: 38,
  Semicolon: 39,
  Quote: 40,
  Backquote: 41,
  ShiftLeft: 42,
  Backslash: 43,
  KeyZ: 44,
  KeyX: 45,
  KeyC: 46,
  KeyV: 47,
  KeyB: 48,
  KeyN: 49,
  KeyM: 50,
  Comma: 51,
  Period: 52,
  Slash: 53,
  ShiftRight: 54,
  NumpadMultiply: 55,
  AltLeft: 56,
  Space: 57,
  CapsLock: 58,
  F1: 59,
  F2: 60,
  F3: 61,
  F4: 62,
  F5: 63,
  F6: 64,
  F7: 65,
  F8: 66,
  F9: 67,
  F10: 68,
  NumLock: 69,
  ScrollLock: 70,
  Numpad7: 71,
  Numpad8: 72,
  Numpad9: 73,
  NumpadSubtract: 74,
  Numpad4: 75,
  Numpad5: 76,
  Numpad6: 77,
  NumpadAdd: 78,
  Numpad1: 79,
  Numpad2: 80,
  Numpad3: 81,
  Numpad0: 82,
  NumpadDecimal: 83,
  IntlBackslash: 86,
  F11: 87,
  F12: 88,
  NumpadEnter: 57372,
  ControlRight: 57373,
  NumpadDivide: 57397,
  AltRight: 57400,
  Home: 57415,
  ArrowUp: 57416,
  PageUp: 57417,
  ArrowLeft: 57419,
  ArrowRight: 57421,
  End: 57423,
  ArrowDown: 57424,
  PageDown: 57425,
  Insert: 57426,
  Delete: 57427,
  MetaLeft: 57435,
  OSLeft: 57435,
  MetaRight: 57436,
  OSRight: 57436,
  ContextMenu: 57437
};
function sendKeyEvent(emu, code, pressed) {
  const base = CODE_TO_SCANCODE[code];
  if (base === void 0) {
    return false;
  }
  const sc = pressed ? base : base > 255 ? base & 65280 | (base & 255 | 128) : base | 128;
  if (sc > 255) {
    void emu.keyboard_send_scancodes([sc >> 8, sc & 255]);
  } else {
    void emu.keyboard_send_scancodes([sc]);
  }
  return true;
}
var LIBV86_URL = "/v86/libv86.js";
var LIBV86_SCRIPT_MARKER = "data-v86-loader";
function ensureSetImmediate() {
  const g = globalThis;
  if (typeof g.setImmediate === "function") {
    return;
  }
  if (typeof MessageChannel !== "undefined") {
    const channel = new MessageChannel();
    const queue = [];
    channel.port1.onmessage = () => {
      const fn = queue.shift();
      if (fn) {
        fn();
      }
    };
    g.setImmediate = (cb, ...args) => {
      queue.push(() => cb.apply(void 0, args));
      channel.port2.postMessage(null);
      return 0;
    };
  } else {
    g.setImmediate = (cb, ...args) => setTimeout(cb, 0, ...args);
  }
}
var defaultLoadV86 = async () => {
  ensureSetImmediate();
  const existing = document.querySelector(
    `script[${LIBV86_SCRIPT_MARKER}]`
  );
  if (!existing) {
    const proc = globalThis.process;
    const savedNode = proc?.versions?.node;
    if (proc?.versions) {
      proc.versions.node = void 0;
    }
    try {
      await new Promise((resolve, reject) => {
        const script = document.createElement("script");
        script.src = LIBV86_URL;
        script.setAttribute(LIBV86_SCRIPT_MARKER, "true");
        script.onload = () => {
          resolve();
        };
        script.onerror = () => {
          reject(new Error(`Failed to load ${LIBV86_URL}`));
        };
        document.head.appendChild(script);
      });
    } finally {
      if (proc?.versions && savedNode !== void 0) {
        proc.versions.node = savedNode;
      }
    }
  }
  const ctor = globalThis.V86;
  if (!ctor) {
    throw new Error("V86 constructor missing on globalThis after libv86 load");
  }
  return ctor;
};
var loadV86Impl = defaultLoadV86;
function _setLoadV86ForTesting(fn) {
  loadV86Impl = fn ?? defaultLoadV86;
}
var src_default = defineApp({
  id: "v86",
  name: "Arch Linux",
  description: "Arch Linux running in a browser-based x86 emulator (v86)",
  icon: "/icons/icon_023_64x64.png",
  singleton: true,
  window: {
    title: "Arch Linux",
    size: { width: V86_WIDTH, height: V86_HEIGHT },
    constraints: {
      minWidth: V86_WIDTH,
      minHeight: V86_HEIGHT,
      maxWidth: V86_WIDTH,
      maxHeight: V86_HEIGHT
    }
  },
  mount(container) {
    const screen = document.createElement("div");
    screen.className = "v86-screen";
    screen.style.cssText = "width:100%;height:100%;position:relative;background:black;overflow:hidden;outline:none;";
    screen.tabIndex = 0;
    const textLayer = document.createElement("div");
    textLayer.style.cssText = "white-space:pre;font:14px monospace;line-height:14px;color:#fff;";
    screen.appendChild(textLayer);
    const canvas = document.createElement("canvas");
    canvas.style.display = "none";
    screen.appendChild(canvas);
    const overlay = document.createElement("div");
    overlay.className = "v86-overlay";
    overlay.style.cssText = "position:absolute;inset:0;display:flex;align-items:center;justify-content:center;color:#fff;font:13px monospace;text-align:center;padding:1em;background:rgba(0,0,0,0.85);";
    overlay.textContent = "Booting Arch Linux\u2026  (first load downloads ~200 MB)";
    screen.appendChild(overlay);
    container.appendChild(screen);
    let emulator = null;
    let disposed = false;
    const stealFocus = () => {
      screen.focus();
    };
    screen.addEventListener("mousedown", stealFocus);
    const handleKeyDown = (ev) => {
      if (!emulator) {
        return;
      }
      if (sendKeyEvent(emulator, ev.code, true)) {
        ev.preventDefault();
        ev.stopPropagation();
      }
    };
    const handleKeyUp = (ev) => {
      if (!emulator) {
        return;
      }
      if (sendKeyEvent(emulator, ev.code, false)) {
        ev.preventDefault();
        ev.stopPropagation();
      }
    };
    screen.addEventListener("keydown", handleKeyDown);
    screen.addEventListener("keyup", handleKeyUp);
    const timeoutId = setTimeout(() => {
      if (disposed) {
        return;
      }
      if (overlay.isConnected) {
        overlay.textContent = "Boot is taking longer than expected. Check the browser console for v86 errors.";
      }
    }, BOOT_TIMEOUT_MS);
    void (async () => {
      try {
        const V86 = await loadV86Impl();
        if (disposed) {
          return;
        }
        emulator = new V86({
          wasm_path: "/v86/v86.wasm",
          memory_size: 512 * 1024 * 1024,
          vga_memory_size: 8 * 1024 * 1024,
          screen_container: screen,
          bios: { url: "/v86/seabios.bin" },
          vga_bios: { url: "/v86/vgabios.bin" },
          // State snapshot of a fully booted Arch Linux. .zst is decompressed
          // by v86's built-in zstd decoder. Sourced from copy.sh's prebuilt
          // demo and staged locally so it can be served same-origin (required
          // by COEP=require-corp).
          initial_state: { url: "/v86/arch_state-v3.bin.zst" },
          // 9p filesystem. basefs is the manifest JSON listing the tree;
          // baseurl is where v86 lazy-fetches individual file chunks. The
          // manifest is required for the Arch state file to deserialize
          // cleanly. baseurl uses our same-origin proxy
          // (vite.config.ts → server.proxy["/v86/arch/"] forwards to
          // i.copy.sh) so chunk fetches stay under COEP=require-corp and
          // the guest can actually read files at runtime.
          filesystem: {
            basefs: { url: "/v86/fs.json" },
            baseurl: "/v86/arch/"
          },
          // copy.sh's archlinux profile configures a virtio NIC. The state
          // file references that device structure; omitting it leaves slots
          // null and produces the `Cannot read properties of null` error
          // during state restore.
          net_device: { type: "virtio" },
          autostart: true
        });
        window.__v86 = emulator;
        emulator.add_listener("emulator-ready", () => {
          try {
            if (!emulator?.is_running()) {
              emulator?.run();
            }
          } catch (e) {
            console.warn("[app-v86] run() failed:", e);
          }
          if (overlay.isConnected) {
            overlay.remove();
          }
        });
      } catch (err) {
        console.error("[app-v86] init failed:", err);
        if (disposed) {
          return;
        }
        if (overlay.isConnected) {
          overlay.textContent = "Could not start Arch Linux. Likely cause: missing assets in /v86/. Run `bun run setup-v86` and refresh. See browser console for details.";
        }
      }
    })();
    return {
      dispose() {
        disposed = true;
        clearTimeout(timeoutId);
        screen.removeEventListener("mousedown", stealFocus);
        screen.removeEventListener("keydown", handleKeyDown);
        screen.removeEventListener("keyup", handleKeyUp);
        try {
          emulator?.destroy();
        } catch (e) {
          console.warn("[app-v86] destroy failed:", e);
        }
        if (screen.parentNode) {
          screen.parentNode.removeChild(screen);
        }
      },
      onFocus() {
        screen.focus();
      }
    };
  }
});
export {
  V86_HEIGHT,
  V86_WIDTH,
  _setLoadV86ForTesting,
  src_default as default,
  defaultLoadV86
};
